import { useContext, useEffect, useState } from 'react';

import { COMMERCE_BACKEND_CCP, COMMERCE_BACKEND_HAMS } from '@atlassian/commerce-ui/rollout';

import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';

import { ccpIsInvoiceable, hamsHasPaymentMethod } from './hasBillingInformation';
import { useCommerceBackendName } from './useCommerceBackendName';

export interface UseApiBillingInformationResponse {
	hasBillingInformation: boolean;
	loading: boolean;
	error: Error | null;
}

export const useApiBillingInformation = (
	skip: boolean = false,
): UseApiBillingInformationResponse => {
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { cloudId } = useSessionData();
	const shouldSkip = skip || !isSiteAdmin || !cloudId;

	const [loading, setLoading] = useState<boolean>(!shouldSkip);
	const [error, setError] = useState<Error | null>(null);
	const [hasBillingInformation, setHasBillingInformation] = useState<boolean>(false);

	const {
		commerceBackendName,
		loading: commerceBackendNameLoading,
		error: commerceBackendNameError,
	} = useCommerceBackendName(shouldSkip);

	useEffect(() => {
		if (shouldSkip) {
			return;
		}
		if (commerceBackendNameError) {
			setError(commerceBackendNameError);
			setLoading(false);
		} else if (!commerceBackendNameLoading && commerceBackendName) {
			const loadHasBillingInformation = async () => {
				try {
					if (commerceBackendName === COMMERCE_BACKEND_CCP) {
						setHasBillingInformation(await ccpIsInvoiceable());
					} else if (commerceBackendName === COMMERCE_BACKEND_HAMS) {
						setHasBillingInformation(await hamsHasPaymentMethod(cloudId));
					} else {
						throw new Error('unknown commerceBackendName');
					}
				} catch (error) {
					setError(new Error(`useHasBillingInformation: ${error.message}`));
					setHasBillingInformation(false);
				} finally {
					setLoading(false);
				}
			};
			void loadHasBillingInformation();
		}
	}, [
		commerceBackendNameLoading,
		commerceBackendNameError,
		commerceBackendName,
		cloudId,
		shouldSkip,
	]);

	return {
		hasBillingInformation,
		loading,
		error,
	};
};
